package com.videoanalyzer.web.models

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Frontend data models.
 *
 * These mirror the backend models for API communication.
 * In a larger project, you'd share these via a common module.
 */

// ============================================================
// User & Auth Models
// ============================================================

@Serializable
data class UserProfile(
    val id: String,
    val email: String,
    @SerialName("subscription_tier")
    val subscriptionTier: String = "free",
    @SerialName("stripe_customer_id")
    val stripeCustomerId: String? = null,
    @SerialName("analyses_this_month")
    val analysesThisMonth: Int = 0,
    @SerialName("analyses_reset_date")
    val analysesResetDate: String? = null,
    @SerialName("credit_balance")
    val creditBalance: Int = 0,
    @SerialName("analyses_today")
    val analysesToday: Int = 0,
    @SerialName("last_analysis_date")
    val lastAnalysisDate: String? = null,
    @SerialName("created_at")
    val createdAt: String? = null,
    @SerialName("cancel_at_period_end")
    val cancelAtPeriodEnd: Boolean = false,
    @SerialName("subscription_end_date")
    val subscriptionEndDate: String? = null
)

@Serializable
data class AuthResponse(
    val user: UserProfile,
    val analysesRemaining: Int
)

// ============================================================
// Video Models
// ============================================================

@Serializable
data class VideoMetadata(
    val id: String,
    val title: String,
    val description: String,
    val publishedAt: String,
    val thumbnailUrl: String?,
    val viewCount: Long,
    val likeCount: Long,
    val commentCount: Long,
    val channelTitle: String,
    val subscriberCount: Long,
    val category: String,
    val duration: String?
)

// ============================================================
// Analysis Models
// ============================================================

@Serializable
data class TitleAnalysis(
    val scores: TitleScores,
    val overallScore: Int,
    val suggestions: List<String>,
    val improvedTitle: String,
    val originalTitle: String = ""
)

@Serializable
data class TitleScores(
    val curiosityGap: ScoreFactor,
    val clarity: ScoreFactor,
    val emotionalTrigger: ScoreFactor,
    val keywords: ScoreFactor,
    val length: ScoreFactor
)

@Serializable
data class ScoreFactor(
    val score: Int,
    val reason: String
)

@Serializable
data class ThumbnailAnalysis(
    val overallScore: Int,
    val scores: ThumbnailScores,
    val detectedElements: DetectedElements,
    val suggestions: List<String>,
    val thumbnailUrl: String = ""
)

@Serializable
data class ThumbnailScores(
    val faceScore: Int,
    val textScore: Int,
    val colorScore: Int,
    val compositionScore: Int,
    val details: ThumbnailScoreDetails? = null
)

@Serializable
data class ThumbnailScoreDetails(
    val hasFace: Boolean = false,
    val faceCount: Int = 0,
    val faceEmotion: String? = null,
    val faceProminence: Float = 0f,
    val hasText: Boolean = false,
    val textContent: List<String> = emptyList(),
    val textWordCount: Int = 0,
    val dominantColors: List<String> = emptyList(),
    val colorContrast: String = "low",
    val brightness: String = "medium",
    val objectCount: Int = 0,
    val isCluttered: Boolean = false
)

@Serializable
data class DetectedElements(
    val faces: List<DetectedFace> = emptyList(),
    val text: List<String> = emptyList(),
    val objects: List<String> = emptyList(),
    val colors: List<ColorInfo> = emptyList()
)

@Serializable
data class DetectedFace(
    val emotion: String,
    val confidence: Float
)

@Serializable
data class ColorInfo(
    val hex: String,
    val name: String,
    val percentage: Float
)

@Serializable
data class FullAnalysis(
    val id: String? = null,
    val videoId: String,
    val videoTitle: String,
    val videoThumbnailUrl: String? = null,
    val overallScore: Int,
    val titleAnalysis: TitleAnalysis,
    val thumbnailAnalysis: ThumbnailAnalysis? = null,
    // Content & SEO analyses
    val descriptionAnalysis: DescriptionAnalysis? = null,
    val seoTagsAnalysis: SeoTagsAnalysis? = null,
    val chaptersAnalysis: ChaptersAnalysis? = null,
    // Performance & Engagement analyses
    val performanceAnalysis: PerformanceAnalysis? = null,
    val engagementAnalysis: EngagementAnalysis? = null,
    val topicDemandAnalysis: TopicDemandAnalysis? = null,
    // Audience & Retention analyses
    val retentionAnalysis: RetentionAnalysis? = null,
    val nicheAnalysis: NicheAnalysis? = null,
    // Timing & Consistency analyses
    val timingAnalysis: TimingAnalysis? = null,
    val pacingAnalysis: PacingAnalysis? = null,
    val consistencyAnalysis: ConsistencyAnalysis? = null,
    // Strategy & Channel insights
    val strategyRecommendations: StrategyRecommendations? = null,
    val channelInsights: ChannelInsights? = null,
    val topRecommendations: List<String>,
    val analyzedAt: String? = null,
    val userId: String? = null
)

// ============================================================
// Extended Analysis Models
// ============================================================

@Serializable
data class DescriptionAnalysis(
    val score: Int = 50,
    val firstLinesPreview: String = "",
    val hasCallToAction: Boolean = false,
    val hasTimestamps: Boolean = false,
    val hasLinks: Boolean = false,
    val hasHashtags: Boolean = false,
    val keywordsFound: List<String> = emptyList(),
    val suggestions: List<String> = emptyList()
)

@Serializable
data class SeoTagsAnalysis(
    val score: Int = 50,
    val tagsCount: Int = 0,
    val tagsQuality: String = "average",
    val missingKeywords: List<String> = emptyList(),
    val suggestedTags: List<String> = emptyList(),
    val suggestions: List<String> = emptyList()
)

@Serializable
data class ChaptersAnalysis(
    val score: Int = 50,
    val hasChapters: Boolean = false,
    val chaptersCount: Int = 0,
    val chaptersQuality: String = "none",
    val suggestions: List<String> = emptyList()
)

@Serializable
data class PerformanceAnalysis(
    val currentViews: Long = 0,
    val currentLikes: Long = 0,
    val currentComments: Long = 0,
    val performanceRating: String = "average",
    val performanceInsight: String = "",
    val engagementRate: Double = 0.0,
    val engagementRating: String = "average",
    val potentialScore: Int = 50,
    val potentialInsight: String = ""
)

@Serializable
data class TopicDemandAnalysis(
    val score: Int = 50,
    val topicRelevance: String = "average",
    val searchDemand: String = "medium",
    val competitionLevel: String = "medium",
    val trendingScore: Int = 50,
    val suggestions: List<String> = emptyList()
)

@Serializable
data class EngagementAnalysis(
    val score: Int = 50,
    val likeToViewRatio: Double = 0.0,
    val commentToViewRatio: Double = 0.0,
    val engagementQuality: String = "average",
    val viralPotential: String = "low",
    val suggestions: List<String> = emptyList()
)

@Serializable
data class RetentionAnalysis(
    val score: Int = 50,
    val hookStrength: String = "average",
    val estimatedRetention: String = "average",
    val pacingTips: List<String> = emptyList(),
    val hookSuggestions: List<String> = emptyList(),
    val suggestions: List<String> = emptyList()
)

@Serializable
data class TimingAnalysis(
    val score: Int = 50,
    val publishedDay: String = "",
    val publishedTime: String = "",
    val isOptimalTime: Boolean = false,
    val bestDays: List<String> = emptyList(),
    val bestTimes: List<String> = emptyList(),
    val suggestions: List<String> = emptyList()
)

@Serializable
data class NicheAnalysis(
    val score: Int = 50,
    val nicheClarity: String = "average",
    val audienceMatch: String = "average",
    val nicheAuthority: String = "building",
    val targetAudience: String = "",
    val suggestions: List<String> = emptyList()
)

@Serializable
data class PacingAnalysis(
    val score: Int = 50,
    val videoDuration: String = "",
    val idealDuration: String = "",
    val durationFit: String = "average",
    val editingTips: List<String> = emptyList(),
    val suggestions: List<String> = emptyList()
)

@Serializable
data class ConsistencyAnalysis(
    val score: Int = 50,
    val channelVideoCount: Int = 0,
    val consistencyRating: String = "unknown",
    val suggestions: List<String> = emptyList()
)

@Serializable
data class StrategyRecommendations(
    val uploadTimingTip: String = "",
    val shortsRecommendation: String = "",
    val seriesTip: String = "",
    val engagementTips: List<String> = emptyList(),
    val growthTips: List<String> = emptyList(),
    val seoTips: List<String> = emptyList()
)

@Serializable
data class ChannelInsights(
    val subscriberCount: Long = 0,
    val channelSize: String = "small",
    val nicheTips: List<String> = emptyList(),
    val competitorInsight: String = ""
)

@Serializable
data class AnalysisSummary(
    val id: String,
    val videoId: String,
    val videoTitle: String,
    val overallScore: Int,
    val titleScore: Int,
    val thumbnailScore: Int,
    val analyzedAt: String
)

@Serializable
data class AnalysisHistoryResponse(
    val analyses: List<AnalysisSummary>,
    val total: Int,
    val hasMore: Boolean
)

// ============================================================
// API Response Wrappers
// ============================================================

@Serializable
data class AnalysisResponse(
    val analysis: FullAnalysis,
    val isExisting: Boolean,
    val message: String? = null,
    // Gating info for freemium model
    val isFirstAnalysis: Boolean = true,  // True = show full, False = gate recommendations
    val totalRecommendationsCount: Int = 0,
    val freeRecommendationsLimit: Int = 2
)

@Serializable
data class ErrorResponse(
    val error: String
)

@Serializable
data class UsageLimitResponse(
    val error: String,
    val currentUsage: Int,
    val limit: Int,
    val upgradeRequired: Boolean
)

// ============================================================
// Payment Models
// ============================================================

@Serializable
data class CheckoutResponse(
    val checkoutUrl: String,
    val sessionId: String
)

@Serializable
data class SubscriptionStatus(
    val isActive: Boolean,
    val tier: String,
    val currentPeriodEnd: String? = null,
    val cancelAtPeriodEnd: Boolean = false,
    val customerId: String? = null
)

@Serializable
data class CreditPack(
    val priceId: String,
    val credits: Int,
    val priceInCents: Int,
    val name: String
)

@Serializable
data class CreditPacksResponse(
    val packs: List<CreditPack>,
    val currentBalance: Int
)

// ============================================================
// Trends Models (Phase 3 - Retention)
// ============================================================

@Serializable
data class TrendsSummary(
    val totalAnalyses: Int,
    val averageScore: Int,
    val trend: String,  // "improving", "declining", "stable", "insufficient", "none"
    val trendDescription: String,
    val recentScores: List<ScorePoint>,
    val needsReanalysis: List<ReanalyzeReminder>
)

@Serializable
data class ScorePoint(
    val date: String,
    val score: Int,
    val videoTitle: String
)

@Serializable
data class ReanalyzeReminder(
    val analysisId: String,
    val videoId: String,
    val videoTitle: String,
    val oldScore: Int,
    val analyzedAt: String,
    val reason: String
)
